import React from 'react';
import { useIsHeiDomain } from 'app/hooks/useIsHeiDomain.ts';
import { Redirect, Route, Switch } from 'react-router';
import { CircularProgress, Box } from '@mui/material';

import { UserRole } from '@/api-gen/api.schemas';
import AppRoutes, { CourseManagementRoutes } from 'app/utils/routes.ts';
import { FallbackPage } from 'app/components/FallbackPage/index.tsx';
import { ProtectedRoute } from 'app/components/ProtectedRoute/index.tsx';
import GetPermissionForRecords from 'app/components/GetPermissionForRecords/getPermissionForRecords.tsx';
import { ErrorBoundary } from 'app/components/ErrorBoundary/ErrorBoundary.tsx';
import { lazyWithRetry, lazyWithRetryNamed } from 'app/utils/lazyWithRetry.ts';

// Lazy-loaded heavyweight components — uses lazyWithRetry to gracefully handle
// chunk load failures after new deployments (auto-reloads once).
const Conversations = lazyWithRetryNamed(
  () => import('app/components/ConversationsPage/index.tsx'),
  (m) => m.Conversations
);
const UsersPage = lazyWithRetry(() => import('app/components/UsersPage/UsersPage.tsx'));
const MyCourses = lazyWithRetry(() => import('app/components/CourseManagement/MyCourses.tsx'));
const CallManagement = lazyWithRetry(() => import('app/components/CallManagement/CallManagement.tsx'));
const GroupManagement = lazyWithRetry(() => import('app/components/GroupManagement/GroupManagement.tsx'));
const CourseManagement = lazyWithRetry(() => import('app/components/CourseManagement/CourseManagement.tsx'));
const ScenarioEditorPage = lazyWithRetry(() => import('app/components/ScenarioEditorPage/ScenarioEditorPage.tsx'));
const GlobalAssetsLibrary = lazyWithRetry(() => import('app/components/GlobalAssetsLibrary/GlobalAssetsLibrary.tsx'));
const EvaluationProtocolsManager = lazyWithRetry(
  () => import('app/components/EvaluationProtocolsManager/EvaluationProtocolsManager.tsx')
);
const PersonasManager = lazyWithRetry(() => import('app/components/PersonasManager/PersonasManager.tsx'));
const ExercisesManager = lazyWithRetry(() => import('app/components/Exercises/ExercisesManager.tsx'));
const ExerciseDetailPage = lazyWithRetry(() => import('app/components/Exercises/ExerciseDetailPage.tsx'));
const Translator = lazyWithRetry(() => import('app/components/Translator/Translator.tsx'));
const StatisticsPage = lazyWithRetry(() => import('./StatisticsPage/StatisticsPage'));
const HistoricalStatisticsPage = lazyWithRetry(() => import('./StatisticsPage/HistoricalStatisticsPage'));
const UsageStatisticsPage = lazyWithRetry(() => import('../../features/usageStatistics/components/UsageStatisticsPage'));
const CompanyManagementPage = lazyWithRetry(() => import('./CompanyManagementPage/CompanyManagementPage.tsx'));
const CompanyDetailsPage = lazyWithRetry(() => import('./CompanyDetailsPage/CompanyDetailsPage.tsx'));

// Additional components that should be lazy-loaded to reduce bundle size
const UserGuides = lazyWithRetry(() => import('app/components/UserGuidesPage/UserGuidesPage.tsx'));
const ScenarioPlayerPage = lazyWithRetry(() => import('./ScenarioPlayerPage/ScenarioPlayerPage.tsx'));
const RecentJobsPage = lazyWithRetry(() => import('./RecentJobsPage'));
const DashboardPage = lazyWithRetry(() => import('./DashboardPage/DashboardPage.tsx'));
const ScenariosPage = lazyWithRetry(() => import('./ScenariosPage/ScenariosPage.tsx'));

import 'app/components/AppInsights.tsx';

const App: React.FC = () => {
  const isHeiDomain = useIsHeiDomain();

  const LoadingFallback = (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px" sx={{ mt: 4 }}>
      <CircularProgress size={40} />
    </Box>
  );

  return (
    <React.Fragment>
      <GetPermissionForRecords />
      <ErrorBoundary>
      <React.Suspense fallback={LoadingFallback}>
        <Switch>
          <ProtectedRoute path={AppRoutes.SceneWithId} component={ScenarioEditorPage} />
          <ProtectedRoute exact path={AppRoutes.ScenarioPlayerNotes} component={ScenarioPlayerPage} />
          <ProtectedRoute exact path={AppRoutes.ScenarioPlayerCompleted} component={ScenarioPlayerPage} />
          <ProtectedRoute exact path={AppRoutes.ScenarioPlayer} component={ScenarioPlayerPage} />
          <ProtectedRoute
            roles={[UserRole.Member, UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            path={AppRoutes.UserGuides}
            component={UserGuides}
          />
          <ProtectedRoute exact path={AppRoutes.Dashboard} component={DashboardPage} />
          <ProtectedRoute exact path={AppRoutes.Conversations} component={Conversations} />
          <ProtectedRoute path={AppRoutes.ConversationsWithId} component={Conversations} />
          <ProtectedRoute exact path={AppRoutes.Statistics} component={StatisticsPage} />
          <ProtectedRoute exact path={AppRoutes.UsageStatistics} component={UsageStatisticsPage} />
          <ProtectedRoute path={AppRoutes.Scenarios} component={ScenariosPage} />
          <ProtectedRoute path={AppRoutes.MyDashboard} component={DashboardPage} />

          <ProtectedRoute
            path={`${AppRoutes.GroupManagement}/:id?`}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={GroupManagement}
          />
          <ProtectedRoute
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            path={CourseManagementRoutes.Root}
            component={CourseManagement}
          />
          <ProtectedRoute
            roles={[UserRole.Member, UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            path={AppRoutes.MyCourses}
            component={MyCourses}
          />

          <ProtectedRoute
            allowInterpreter
            requireSklls={false}
            roles={[UserRole.Owner, UserRole.SuperAdmin]}
            path={AppRoutes.Translator}
            component={Translator}
          />

          <ProtectedRoute
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            path={[AppRoutes.UserInvitations, `${AppRoutes.UserManagement}/:id?`]}
            component={UsersPage}
          />

          <ProtectedRoute exact path={AppRoutes.Root}>
            <Redirect to={isHeiDomain ? '/translator' : '/dashboard'} />
          </ProtectedRoute>
          <ProtectedRoute
            exact
            path={`${AppRoutes.GlobalAssetsLibrary}/:id?`}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={GlobalAssetsLibrary}
          />
          <ProtectedRoute
            exact
            path={AppRoutes.CallManagement}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={CallManagement}
          />
          <ProtectedRoute
            exact
            path={`${AppRoutes.EvaluationProtocolManager}/:id?`}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={EvaluationProtocolsManager}
          />
          <ProtectedRoute
            exact
            path={`${AppRoutes.PersonasManager}/:id?`}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={PersonasManager}
          />
          <ProtectedRoute
            exact
            path={AppRoutes.ExercisesManager}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={ExercisesManager}
          />
          <ProtectedRoute
            exact
            path={AppRoutes.ExerciseWithId}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={ExerciseDetailPage}
          />
          <ProtectedRoute
            exact
            path={AppRoutes.RecentJobs}
            roles={[UserRole.Admin, UserRole.Owner, UserRole.SuperAdmin]}
            component={RecentJobsPage}
          />
          <ProtectedRoute
            exact
            path={AppRoutes.CompaniesManagement}
            roles={[UserRole.SuperAdmin]}
            component={CompanyManagementPage}
          />
          <ProtectedRoute
            exact
            path={AppRoutes.CompanyDetail}
            roles={[UserRole.SuperAdmin]}
            component={CompanyDetailsPage}
          />
          <Route render={() => <FallbackPage autoGenerateDiagnosticId />} />
        </Switch>
      </React.Suspense>
      </ErrorBoundary>
    </React.Fragment>
  );
};

export default App;
